import { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Sparkles, Lightbulb, ThumbsUp } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RewritePanel } from "@/components/RewritePanel";

export const MIN_GRADE_CHARS = 100;

export const gradeTone = (score) => (score >= 90 ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" : score >= 80 ? "bg-sky-400/15 text-sky-300 border-sky-400/40" : score >= 70 ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40");

const STATUS_ICON = { pass: [CheckCircle2, "text-emerald-400"], warn: [AlertTriangle, "text-amber-300"], fail: [XCircle, "text-rose-400"] };

export const useSummaryGrade = (title, description) => {
  const [grade, setGrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    const text = description.trim();
    if (text.length < MIN_GRADE_CHARS) { setGrade(null); setLoading(false); setError(""); return; }
    setLoading(true); setError("");
    const id = ++seq.current;
    const t = setTimeout(() => {
      api.post("/portal/summary-grade", { title, description: text })
        .then((r) => { if (seq.current === id) setGrade(r.data); })
        .catch((e) => { if (seq.current === id) { setGrade(null); setError(e?.response?.data?.detail || "Grading is unavailable right now"); } })
        .finally(() => { if (seq.current === id) setLoading(false); });
    }, 1500);
    return () => clearTimeout(t);
  }, [title, description, tick]);
  return { grade, loading, error, retry: () => setTick((n) => n + 1) };
};

const CheckRow = ({ c, i }) => {
  const [Icon, cls] = STATUS_ICON[c.status] || STATUS_ICON.warn;
  return (
    <li className="flex items-start gap-3 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2.5" data-testid={`grade-check-${c.id}`}>
      <Icon size={16} className={`${cls} mt-0.5 shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold">{c.label}</p><span className="font-mono text-[11px] text-slate-400">{c.earned}/{c.points}</span></div>
        <p className="text-xs text-slate-400 leading-relaxed">{c.note}</p>
      </div>
    </li>
  );
};

export const SummaryGradeDialog = ({ grade, open, onOpenChange, rewrite }) => {
  if (!grade) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0B1526] border-white/10 text-white" data-testid="grade-dialog">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <span className={`inline-flex h-16 w-16 items-center justify-center rounded-2xl border font-display text-3xl font-black ${gradeTone(grade.score)}`} data-testid="grade-dialog-letter">{grade.grade}</span>
            <div>
              <DialogTitle className="font-display text-xl font-extrabold">Summary Grade · {grade.score}/100</DialogTitle>
              <DialogDescription className="text-slate-400 mt-1" data-testid="grade-dialog-verdict">{grade.verdict}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {rewrite && <RewritePanel title={rewrite.title} description={rewrite.description} onUse={rewrite.onUse} tone={gradeTone} />}
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-4" data-testid="grade-strengths">
            <p className="eyebrow !text-emerald-300 flex items-center gap-2"><ThumbsUp size={13} /> What's working</p>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-200">{(grade.strengths || []).map((s) => <li key={s} className="flex gap-2"><span className="text-emerald-400">•</span><span>{s}</span></li>)}{!grade.strengths?.length && <li className="text-slate-500">Nothing yet — keep going.</li>}</ul>
          </div>
          <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-4" data-testid="grade-improvements">
            <p className="eyebrow !text-amber-300 flex items-center gap-2"><Lightbulb size={13} /> How to improve</p>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-200">{(grade.improvements || []).map((s) => <li key={s} className="flex gap-2"><span className="text-amber-300">•</span><span>{s}</span></li>)}{!grade.improvements?.length && <li className="text-slate-500">Nothing major — nice work.</li>}</ul>
          </div>
        </div>
        <div className="mt-2">
          <p className="eyebrow flex items-center gap-2"><Sparkles size={13} /> Scorecard</p>
          <ul className="mt-2 grid sm:grid-cols-2 gap-2">{grade.checks.map((c, i) => <CheckRow key={c.id} c={c} i={i} />)}</ul>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">Graded against the Underdog SEO summary rules: 400–1,000 characters (550–850 ideal) for the summary itself — timestamps, links and footers below it aren't counted{grade.has_extras ? ` (${grade.length} of ${grade.total_length} characters graded)` : ""}. Subject and angle in the first 250 characters, no filler openings, natural keywords, short 2–3 sentence paragraphs.{!grade.ai && " AI editorial review was unavailable for this pass."}</p>
      </DialogContent>
    </Dialog>
  );
};

export const SummaryGradePill = ({ grade, loading, error, onRetry, onClick }) => {
  if (loading && !grade) return <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-0.5 text-[11px] text-slate-400" data-testid="grade-loading"><Loader2 size={11} className="animate-spin" /> Grading…</span>;
  if (!grade && error) return <button type="button" onClick={onRetry} className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 px-2.5 py-0.5 text-[11px] text-rose-200 hover:bg-rose-500/20" data-testid="grade-error"><AlertTriangle size={11} /> {String(error).slice(0, 80)} · retry</button>;
  if (!grade) return null;
  return (
    <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold transition-transform hover:scale-105 ${gradeTone(grade.score)} ${loading ? "opacity-60" : ""}`} title="See why — click for the full breakdown" data-testid="grade-pill">
      {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Summary Grade: <span className="font-display text-sm leading-none" data-testid="grade-pill-letter">{grade.grade}</span>
    </button>
  );
};
